// Zod-схемы namespace `auth` — вход, сессии, устройства, журнал безопасности.
// Источник: docs/DOMAIN-MODEL.md §1 + макет «IEC ID Auth.dc.html» (4a) и
// «IEC ID Sections.dc.html» (3d · Security).
import { z } from "zod";
import {
	EmailSchema,
	IsoDateTimeSchema,
	LocaleSchema,
	MaskedEmailSchema,
	PaginationInputSchema,
	paginated,
	ServiceIdSchema,
	SubjectSchema,
	UuidV7Schema,
} from "../_shared/primitives.js";

/** Роли из `users.role`. Читаются сервисами через userinfo (совместимость §10.21). */
export const UserRoleSchema = z.enum(["user", "moderator", "admin"]);

/** Состояние учётной записи (`users.status`). */
export const UserStatusSchema = z.enum(["active", "disabled", "pending_deletion", "purged"]);

/**
 * Внешние провайдеры входа. Паролей в IEC ID нет и не будет (решение владельца
 * Д1): вход — код из письма и Google, Apple добавится после запуска.
 */
export const AuthProviderSchema = z.enum(["google", "apple"]);

/** Назначение одноразового кода (`email_codes.purpose`). */
export const EmailCodePurposeSchema = z.enum(["sign_in", "link_email", "verify"]);

/** Код из письма — ровно 6 цифр, 10 минут жизни. */
export const EmailCodeSchema = z.string().regex(/^\d{6}$/, "Код — шесть цифр");

/** Отпечаток устройства с клиента. Ключ `devices.fingerprint`. */
export const DeviceFingerprintSchema = z.string().min(8).max(128);

// ─── me ─────────────────────────────────────────────────────────────────────

export const AuthMeOutputSchema = z.object({
	userId: UuidV7Schema,
	/** Ключ связи между сервисами. Не меняется при смене почты. */
	sub: SubjectSchema,
	email: EmailSchema,
	/** Подтверждена ли почта **нашей** стороной (инвариант №2). */
	emailVerified: z.boolean(),
	role: UserRoleSchema,
	status: UserStatusSchema,
	displayName: z.string(),
	nickname: z.string().nullable(),
	avatarUrl: z.url().nullable(),
	locale: LocaleSchema,
	googleLinked: z.boolean(),
	/** Онбординг не пройден — витрина уводит в мастер из пяти шагов. */
	needsOnboarding: z.boolean(),
	/** Есть документы Legal Center, ждущие принятия (см. `legal.pendingAcceptance`). */
	hasPendingLegal: z.boolean(),
	sessionId: UuidV7Schema,
	lastLoginAt: IsoDateTimeSchema.nullable(),
});

// ─── вход по коду ───────────────────────────────────────────────────────────

/** Код приглашения с лендинга `/ref/<code>` (макет 4c). */
export const ReferralCodeSchema = z.string().min(3).max(32);

export const RequestEmailCodeInputSchema = z.object({
	email: EmailSchema,
	purpose: EmailCodePurposeSchema.default("sign_in"),
	/** Язык письма. Без него берётся локаль браузера/профиля. */
	locale: LocaleSchema.optional(),
	/** Сервис, из которого пришёл человек, — для текста письма и возврата. */
	serviceId: ServiceIdSchema.optional(),
	/**
	 * Код приглашения. Хранится ВМЕСТЕ с кодом из письма и доезжает до
	 * `verifyEmailCode`: без этого поля реферальная связь терялась у основного
	 * способа входа — Google-ветка код передавала, почтовая нет.
	 */
	referralCode: ReferralCodeSchema.optional(),
});

export const RequestEmailCodeOutputSchema = z.object({
	sent: z.literal(true),
	/** Через сколько секунд разрешена повторная отправка (троттлинг по email и ip). */
	resendAfterSec: z.number().int().nonnegative(),
	/** Экран кода показывает адрес, но не полностью. */
	maskedEmail: MaskedEmailSchema,
	/** Срок жизни кода — 10 минут (макет 4a). */
	expiresInSec: z.number().int().positive(),
	/** Только dev-окружение: код возвращается, чтобы не поднимать почту. */
	devCode: EmailCodeSchema.optional(),
});

export const VerifyEmailCodeInputSchema = z.object({
	email: EmailSchema,
	code: EmailCodeSchema,
	purpose: EmailCodePurposeSchema.default("sign_in"),
	deviceFingerprint: DeviceFingerprintSchema.optional(),
	/** «Запомнить устройство» — влияет на `devices.trusted`. */
	rememberDevice: z.boolean().default(false),
	serviceId: ServiceIdSchema.optional(),
	/**
	 * Код приглашения. Дублирует поле `requestEmailCode` намеренно: человек мог
	 * запросить код на одном экране, а ввести его на другом (новая вкладка,
	 * письмо открыто на телефоне). Побеждает тот, что дошёл, — сохранённый с
	 * кодом либо этот.
	 */
	referralCode: ReferralCodeSchema.optional(),
});

/**
 * Результат ввода кода — discriminated union по `state` (rules/schema-first.md §2.5).
 * `invalid` покрывает и протухший код: сколько попыток осталось, видно на экране.
 */
export const VerifyEmailCodeOutputSchema = z.discriminatedUnion("state", [
	z.object({
		state: z.literal("signed-in"),
		needsOnboarding: z.boolean(),
		/** Куда вернуть человека после входа (сервис-инициатор или витрина). */
		redirectTo: z.url().nullable(),
	}),
	z.object({
		state: z.literal("invalid"),
		attemptsLeft: z.number().int().nonnegative(),
	}),
	z.object({
		state: z.literal("locked"),
		/** Через сколько секунд можно запросить новый код. */
		retryAfterSec: z.number().int().positive(),
	}),
]);

// ─── Google ─────────────────────────────────────────────────────────────────

/**
 * Отложенный запрос авторизации, который человек не доиграл до конца.
 *
 * Человек пришёл из сервиса на `authorize`, живой сессии у него не было, и мы
 * увели его на форму входа. Дальше он ушёл на `accounts.google.com` — то есть
 * покинул наш домен целиком, вместе со всей адресной строкой. Вернуть его
 * после Google просто «на сайт сервиса» значит вернуть **гостем**: сервис ждёт
 * `code`, а получает пустой заход.
 *
 * Поэтому контекст едет с человеком: в подписанном `state` до Google и обратно,
 * а на возврате мы доигрываем `authorize` за него. Поля — ровно те, что влияют
 * на выдачу кода; всё остальное (`ref`, `finish_page`) уже разошлось по своим
 * местам на первом шаге.
 */
export const PendingAuthorizeSchema = z.object({
	clientId: z.string().min(1).max(128),
	redirectUri: z.string().min(1).max(2048),
	responseType: z.string().max(32).optional(),
	state: z.string().max(2048).optional(),
	scope: z.string().max(512).optional(),
	/** PKCE. Потерять его на пути через Google — молча выключить PKCE. */
	codeChallenge: z.string().max(256).optional(),
	codeChallengeMethod: z.string().max(16).optional(),
	/** OIDC `nonce` клиента — не путать с nonce привязки state к браузеру. */
	nonce: z.string().max(256).optional(),
});

export const GoogleStartInputSchema = z.object({
	/** Абсолютный URL возврата. Проверяется по реестру клиентов. */
	redirectTo: z.url().optional(),
	serviceId: ServiceIdSchema.optional(),
	referralCode: ReferralCodeSchema.optional(),
	/** Незавершённый `authorize`, к которому надо вернуться после Google. */
	authorize: PendingAuthorizeSchema.optional(),
});

export const GoogleStartOutputSchema = z.object({
	authorizationUrl: z.url(),
	/** CSRF-состояние; клиент не разбирает, возвращает как есть. */
	state: z.string().min(8),
});

export const LinkGoogleInputSchema = z.object({
	redirectTo: z.url().optional(),
});

export const LinkGoogleOutputSchema = GoogleStartOutputSchema;

export const UnlinkGoogleOutputSchema = z.object({
	unlinked: z.literal(true),
	/** Что осталось из способов входа — отвязать последний нельзя. */
	remainingMethods: z.array(z.enum(["email_code", "google", "apple"])),
});

// ─── account chooser ────────────────────────────────────────────────────────

export const SessionHintInputSchema = z.object({
	/** Сервис, который спрашивает «Продолжить как …». */
	serviceId: ServiceIdSchema.optional(),
});

/**
 * Подсказка для chooser (инвариант №5): полную почту не отдаём, `sub` — тем более.
 * Аккаунт впрок не создаётся: связь пишется только после реального входа.
 */
export const SessionHintOutputSchema = z.discriminatedUnion("state", [
	z.object({ state: z.literal("none") }),
	z.object({
		state: z.literal("session"),
		displayName: z.string(),
		maskedEmail: MaskedEmailSchema,
		avatarUrl: z.url().nullable(),
		/** Что сервис получит после входа — строка показа в макете 4a. */
		sharedFields: z.array(z.string()),
	}),
]);

// ─── сессии и устройства ────────────────────────────────────────────────────

export const SessionSchema = z.object({
	id: UuidV7Schema,
	/** Текущая сессия — её нельзя отозвать кнопкой «Выйти» из списка. */
	current: z.boolean(),
	deviceId: UuidV7Schema.nullable(),
	deviceLabel: z.string().nullable(),
	browser: z.string().nullable(),
	os: z.string().nullable(),
	city: z.string().nullable(),
	country: z.string().nullable(),
	createdAt: IsoDateTimeSchema,
	lastSeenAt: IsoDateTimeSchema,
	/**
	 * Скользящий срок: год с последней активности в ЛЮБОМ сервисе экосистемы,
	 * а не с момента входа. Значение сдвигается вперёд, пока человек
	 * пользуется хоть чем-то, — то есть у активного человека вход не истекает.
	 * Заканчивают его отзывом (выход, бан, удаление), а не сроком.
	 */
	expiresAt: IsoDateTimeSchema,
});

export const ListSessionsOutputSchema = z.object({
	items: z.array(SessionSchema),
});

export const RevokeSessionInputSchema = z.object({
	sessionId: UuidV7Schema,
});

export const RevokeSessionOutputSchema = z.object({
	revoked: z.literal(true),
	sessionId: UuidV7Schema,
});

export const RevokeAllSessionsInputSchema = z.object({
	/** «Выйти везде» обычно оставляет текущую вкладку живой. */
	exceptCurrent: z.boolean().default(true),
});

export const RevokeAllSessionsOutputSchema = z.object({
	revokedCount: z.number().int().nonnegative(),
});

/**
 * Вход в СЕРВИСЕ экосистемы — третий список раздела «Безопасность».
 *
 * «Входы» — про наши сессии и устройства, «Сервисы» — про право читать
 * профиль, а это про сеанс: где прямо сейчас живёт доступ, выданный сервису от
 * имени человека. Живым считается непогашенный refresh-токен, а не access:
 * тот истекает за час и у человека, отошедшего от компьютера, сервис «пропадал
 * бы» из списка сам собой.
 */
export const ServiceSessionSchema = z.object({
	serviceId: ServiceIdSchema,
	/** Когда сервис получил этот доступ. */
	since: IsoDateTimeSchema,
	/** Последняя выдача или ротация пары — «когда сервис приходил». */
	lastSeenAt: IsoDateTimeSchema,
	/** Живых пар токенов: обычно по одной на устройство человека в сервисе. */
	tokens: z.number().int().positive(),
});

export const ServiceSessionsOutputSchema = z.object({
	items: z.array(ServiceSessionSchema),
});

export const RevokeServiceSessionInputSchema = z.object({
	/**
	 * `null` — выйти из ВСЕХ сервисов сразу. Отдельного метода под «все» нет
	 * намеренно: это то же действие с другой областью, и два метода разошлись
	 * бы в поведении при первой же правке.
	 */
	serviceId: ServiceIdSchema.nullable(),
});

export const RevokeServiceSessionOutputSchema = z.object({
	/** Сколько пар токенов погашено. Ноль — выходить было неоткуда. */
	revoked: z.number().int().nonnegative(),
	/** Кому сообщили об отзыве SET-событием. */
	serviceIds: z.array(ServiceIdSchema),
});

export const DeviceSchema = z.object({
	id: UuidV7Schema,
	/**
	 * Читаемая подпись вида «Chrome · macOS». `null` — распознать не удалось;
	 * текст-заглушку подставляет интерфейс на языке пользователя, сервер
	 * готовых фраз не сочиняет (rules/i18n.md).
	 */
	label: z.string().nullable(),
	os: z.string().nullable(),
	browser: z.string().nullable(),
	city: z.string().nullable(),
	country: z.string().nullable(),
	isTrusted: z.boolean(),
	/**
	 * Место этого устройства отличается от места, откуда человек смотрит сейчас.
	 *
	 * Именно это, а не `isTrusted`, и есть повод для тревоги. «Доверенное
	 * устройство» ставится галочкой «Запомнить», которой в беспарольной схеме
	 * нет вовсе, — поэтому раньше НЕ доверенными были все устройства подряд, и
	 * витрина честно-бессмысленно кричала «незнакомое устройство» на каждый
	 * обычный вход.
	 *
	 * `false`, когда любое из двух мест неизвестно: обвинять по отсутствующим
	 * данным нельзя.
	 */
	isUnfamiliarPlace: z.boolean(),
	/** Устройство, с которого открыт текущий экран. */
	isCurrent: z.boolean(),
	/** Есть ли на устройстве живая сессия — от этого зависит кнопка «Выйти». */
	hasActiveSession: z.boolean(),
	firstSeenAt: IsoDateTimeSchema,
	lastSeenAt: IsoDateTimeSchema,
});

export const ListDevicesOutputSchema = z.object({
	items: z.array(DeviceSchema),
});

export const ForgetDeviceInputSchema = z.object({
	deviceId: UuidV7Schema,
	/** Забыть устройство — заодно погасить его сессии. */
	revokeSessions: z.boolean().default(true),
});

export const ForgetDeviceOutputSchema = z.object({
	forgotten: z.literal(true),
	deviceId: UuidV7Schema,
	revokedSessions: z.number().int().nonnegative(),
});

// ─── журнал безопасности ────────────────────────────────────────────────────

/** Виды записей `security_events` (DOMAIN-MODEL §1). */
export const SecurityEventKindSchema = z.enum([
	"sign_in",
	"sign_in_failed",
	"sign_out",
	"device_added",
	"email_changed",
	/**
	 * Значение осталось в типе БД (`ALTER TYPE ... DROP VALUE` в PostgreSQL нет),
	 * но код его не пишет: паролей в IEC ID нет и не будет (Д1). Витрина обязана
	 * уметь его показать только ради исторических строк.
	 */
	"password_reset",
	"session_revoked",
	"consent_changed",
	"deletion_requested",
	/** Привязан внешний способ входа (Google). */
	"identity_linked",
	/** Отвязан внешний способ входа. */
	"identity_unlinked",
	/** Аккаунт связан с анкетой старого портала по доверенной почте. */
	"legacy_linked",
]);

export const SecurityEventSchema = z.object({
	id: UuidV7Schema,
	kind: SecurityEventKindSchema,
	/** IP усечён до /24 (v4) — журнал показывается владельцу, а не расследованию. */
	ip: z.string().nullable(),
	userAgent: z.string().nullable(),
	city: z.string().nullable(),
	country: z.string().nullable(),
	serviceId: ServiceIdSchema.nullable(),
	createdAt: IsoDateTimeSchema,
});

export const SecurityEventsInputSchema = PaginationInputSchema.extend({
	kind: SecurityEventKindSchema.optional(),
});

/** Журнал живёт 180 дней (DOMAIN-MODEL §1) — глубже курсор не уходит. */
export const SecurityEventsOutputSchema = paginated(SecurityEventSchema).extend({
	retentionDays: z.number().int().positive(),
});

// ─── выход ──────────────────────────────────────────────────────────────────

export const SignOutInputSchema = z.object({
	/** Погасить все сессии, а не только текущую. */
	everywhere: z.boolean().default(false),
	redirectTo: z.url().optional(),
});

export const SignOutOutputSchema = z.object({
	signedOut: z.literal(true),
	redirectTo: z.url().nullable(),
});
